import { useMemo } from 'react';
import { sessionState, useAppSelector } from 'src/store';
import { UserTier } from 'src/types';
import { UserSegment } from 'src/types/models/UserSegment';
import { AccountSubscriptionTier } from 'src/types/models/AccountSubscriptionTier';

export const useGetUser = () => {
  const { appUser } = useAppSelector(sessionState);

  const isOpenTier = appUser?.tier_id === UserTier.OPEN || !appUser?.tier_id;

  const isSubscribedTier =
    appUser?.tier_id === UserTier.PAID ||
    appUser?.tier_id === UserTier.STANDARD ||
    appUser?.tier_id === UserTier.PRO ||
    appUser?.tier_id === UserTier.ULTRA ||
    appUser?.tier_id === UserTier.TESTING ||
    appUser?.tier_id === UserTier.ENTERPRISE ||
    appUser?.tier_id === UserTier.TEAMS_STANDARD ||
    appUser?.tier_id === UserTier.TEAMS_PRO ||
    appUser?.tier_id === UserTier.TEAMS_ULTRA ||
    appUser?.tier_id === UserTier.ENTERPRISE_TRIAL ||
    appUser?.tier_id === UserTier.SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.TEAMS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.SUPERNINJA_PRO ||
    appUser?.tier_id === UserTier.SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_PRO ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.INTERNAL_UNLIMITED ||
    appUser?.tier_id === UserTier.SUPERNINJA_STANDARD ||
    appUser?.superninja_tier_id === AccountSubscriptionTier.STARTER ||
    appUser?.tier_id === UserTier.CREDIT_PRO ||
    appUser?.tier_id === UserTier.CREDIT_BUSINESS_PRO;

  const isTrialTier =
    appUser?.tier_id === UserTier.STANDARD_TRIAL ||
    appUser?.tier_id === UserTier.PRO_TRIAL ||
    appUser?.tier_id === UserTier.ULTRA_TRIAL ||
    appUser?.tier_id === UserTier.SUPERNINJA_ULTRA_TRIAL;

  const isProTier =
    appUser?.tier_id === UserTier.PRO ||
    appUser?.tier_id === UserTier.PAID ||
    appUser?.tier_id === UserTier.TESTING;

  const isLimitedTier =
    appUser?.tier_id === UserTier.STANDARD ||
    appUser?.tier_id === UserTier.FREE ||
    appUser?.tier_id === UserTier.OPEN ||
    appUser?.tier_id === UserTier.TEAMS_STANDARD;

  const isTeamsTier =
    appUser?.tier_id === UserTier.TEAMS_PRO ||
    appUser?.tier_id === UserTier.TEAMS_ULTRA ||
    appUser?.tier_id === UserTier.TEAMS_STANDARD ||
    appUser?.tier_id === UserTier.TEAMS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_PRO ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.CREDIT_PRO ||
    appUser?.tier_id === UserTier.CREDIT_BUSINESS_PRO;

  const isMaxTier =
    appUser?.tier_id === UserTier.TEAMS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.SUPERNINJA_PRO ||
    appUser?.tier_id === UserTier.SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_PRO ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_ULTRA ||
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.SUPERNINJA_ULTRA_TRIAL ||
    appUser?.tier_id === UserTier.INTERNAL_UNLIMITED ||
    appUser?.tier_id === UserTier.SUPERNINJA_STANDARD ||
    appUser?.tier_id === UserTier.CREDIT_PRO ||
    appUser?.tier_id === UserTier.CREDIT_BUSINESS_PRO;

  const isUnlimitedMaxTier =
    appUser?.tier_id === UserTier.BUSINESS_SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.SUPERNINJA_MAX ||
    appUser?.tier_id === UserTier.INTERNAL_UNLIMITED;

  const isEducationUser = appUser?.user_segment === UserSegment.EDUCATION;

  const isCreditTier =
    appUser?.tier_id === UserTier.CREDIT_PRO ||
    appUser.tier_id === UserTier.CREDIT_BUSINESS_PRO;

  return useMemo(
    () => ({
      ...appUser,
      isOpenTier,
      isFreeTier: appUser?.tier_id === UserTier.FREE,
      isStarterTier:
        appUser?.superninja_tier_id === AccountSubscriptionTier.STARTER,
      isStandardTier: appUser?.tier_id === UserTier.STANDARD,
      isStandardTrialTier: appUser?.tier_id === UserTier.STANDARD_TRIAL,
      isProTier,
      isUltraTier: appUser?.tier_id === UserTier.ULTRA,
      isUltraTrialTier: appUser?.tier_id === UserTier.ULTRA_TRIAL,
      isSubscribedTier,
      isTrialTier,
      isLimitedTier,
      isProTrialTier: appUser?.tier_id === UserTier.PRO_TRIAL,
      isTestTier: appUser?.tier_id === UserTier.TESTING,
      isEnterpriseTrialTier: appUser?.tier_id === UserTier.ENTERPRISE_TRIAL,
      isEnterpriseTier: appUser?.tier_id === UserTier.ENTERPRISE,
      isTeamsTier,
      isTeamsProTier: appUser.tier_id === UserTier.TEAMS_PRO,
      isTeamsStandardTier: appUser?.tier_id === UserTier.TEAMS_STANDARD,
      isTeamsUltraTier: appUser?.tier_id === UserTier.TEAMS_ULTRA,
      isTeamsMaxTier: appUser?.tier_id === UserTier.TEAMS_SUPERNINJA_ULTRA,
      isSuperNinjaUltraTier: appUser?.tier_id === UserTier.SUPERNINJA_ULTRA,
      isSuperNinjaUltraTrialTier:
        appUser?.tier_id === UserTier.SUPERNINJA_ULTRA_TRIAL,
      tierId: appUser?.tier_id || UserTier.OPEN,
      isMaxTier,
      isUnlimitedMaxTier,
      isEducationUser,
      isCreditTier,
    }),
    [
      appUser,
      isLimitedTier,
      isOpenTier,
      isProTier,
      isTrialTier,
      isSubscribedTier,
      isTeamsTier,
      isMaxTier,
      isUnlimitedMaxTier,
      isEducationUser,
      isCreditTier,
    ],
  );
};
